import { createQueryKeys } from '@lukemorales/query-key-factory';
import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { ProductPolicyApi, MapProductPolicyApi } from 'api';
import type { ProductPolicy } from 'api/methods/ProductPolicyApi';
import type { MapProductPolicy } from 'api/methods/MapProductPolicy';

/**
 * [ProductPolicy] Query key 생성
 * --
 */
export const productPolicyQueryKeys = createQueryKeys('productpolicy', {
  getProductPolicy: (
    idx: number,
    params?: { [key: string]: string | number }
  ) => [idx, params],
  getProductPolicies: (params?: { [key: string]: string | number }) => [params],
});

/**
 * [ProductPolicy] 상품 정책 전체 조회
 * --
 */
export const useGetProductPolicies = (
  params?: { [key: string]: string | number },
  options?: UseQueryOptions<any, any>
) => {
  const data = useQuery({
    queryKey: productPolicyQueryKeys.getProductPolicies(params).queryKey,
    queryFn: () => ProductPolicyApi.getProductPolicies(params),
    ...options,
  });
  return data;
};

/**
 * [ProductPolicy] 상품 정책 상세 조회
 * --
 */
export const useGetProductPolicy = (
  idx: number,
  params?: { [key: string]: string | number }
) => {
  const data = useQuery({
    queryKey: productPolicyQueryKeys.getProductPolicy(idx, params).queryKey,
    queryFn: () => ProductPolicyApi.getProductPolicy(idx, params),
  });

  return data;
};

/**
 * [ProductPolicy] 상품 정책 생성
 * --
 */
export const useCreateProductPolicy = (body: ProductPolicy) => {
  const { mutateAsync: createProductPolicy, ...result } = useMutation({
    mutationFn: (variables: ProductPolicy) =>
      ProductPolicyApi.createProductPolicy(variables),
    onSuccess: (data, variables) => {
      console.log(data, variables);
    },
    onError: (error) => {
      console.error(error);
    },
  });

  return { createProductPolicy, ...result };
};

/**
 * [ProductPolicy] 상품 정책 수정
 * --
 */
export const useUpdateProductPolicy = (body: ProductPolicy) => {
  const { mutateAsync: updateProductPolicy, ...result } = useMutation({
    mutationFn: (variables: ProductPolicy) =>
      ProductPolicyApi.updateProductPolicy(variables),
    onSuccess: (data, variables) => {
      console.log(data, variables);
    },
    onError: (error) => {
      console.error(error);
    },
  });

  return { updateProductPolicy, ...result };
};

/**
 * [ProductPolicy] 상품 정책 삭제
 * --
 */
export const useDeleteProductPolicy = () => {
  const { mutateAsync: deleteProductPolicy, ...result } = useMutation({
    mutationFn: (variables: number) =>
      ProductPolicyApi.deleteProductPolicy(variables),
  });

  return { deleteProductPolicy, ...result };
};

/**
 * [ProductPolicy] 상품 정책 삭제
 * --
 */
export const useBulkUpdateProductPolicy = () => {
  const { mutateAsync: bulkUpdateProductPolicy, ...result } = useMutation({
    mutationFn: (variables: MapProductPolicy[]) =>
      MapProductPolicyApi.updateBulkProductPolicy(variables),
  });

  return { bulkUpdateProductPolicy, ...result };
};
